import { useEffect } from 'react';

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  canonical?: string;
  ogType?: string;
  ogImage?: string;
  noindex?: boolean;
  schema?: object;
}

export const useSEO = ({
  title,
  description,
  keywords,
  canonical,
  ogType = 'website',
  ogImage,
  noindex = false,
  schema,
}: SEOProps) => {
  useEffect(() => {
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
    
    // Title
    if (title) {
      document.title = title;
    }

    // Meta tags
    const metaTags: { [key: string]: string } = {
      description: description || '',
      keywords: keywords || '',
      robots: noindex ? 'noindex, nofollow' : 'index, follow',
      googlebot: noindex ? 'noindex, nofollow' : 'index, follow',
      'og:type': ogType,
      'og:title': title || '',
      'og:description': description || '',
      'og:url': canonical ? `${siteUrl}${canonical}` : siteUrl,
      'og:site_name': 'KR STUDIO',
      'og:locale': 'ko_KR',
      'twitter:card': 'summary_large_image',
      'twitter:title': title || '',
      'twitter:description': description || '',
    };

    if (ogImage) {
      metaTags['og:image'] = ogImage;
      metaTags['twitter:image'] = ogImage;
    }

    // Update or create meta tags
    Object.entries(metaTags).forEach(([key, value]) => {
      if (!value) return;

      const property = key.startsWith('og:') || key.startsWith('twitter:') ? 'property' : 'name';
      let meta = document.querySelector(`meta[${property}="${key}"]`);

      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute(property, key);
        document.head.appendChild(meta);
      }

      meta.setAttribute('content', value);
    });

    // Canonical link
    if (canonical) {
      let link = document.querySelector('link[rel="canonical"]');
      if (!link) {
        link = document.createElement('link');
        link.setAttribute('rel', 'canonical');
        document.head.appendChild(link);
      }
      link.setAttribute('href', `${siteUrl}${canonical}`);
    }

    // Schema.org JSON-LD
    if (schema) {
      const scriptId = 'schema-jsonld';
      let script = document.getElementById(scriptId);
      
      if (!script) {
        script = document.createElement('script');
        script.id = scriptId;
        script.type = 'application/ld+json';
        document.head.appendChild(script);
      }
      
      script.textContent = JSON.stringify(schema);
    }

    return () => {
      // Cleanup schema script on unmount
      if (schema) {
        const script = document.getElementById('schema-jsonld');
        if (script) {
          script.remove();
        }
      }
    };
  }, [title, description, keywords, canonical, ogType, ogImage, noindex, schema]);
};

// Schema.org helpers
export const createOrganizationSchema = () => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'KR STUDIO',
    url: siteUrl,
    logo: `${siteUrl}/vite.svg`,
    description: 'KR스튜디오는 콘텐츠 제작, 라이브 스트리밍, 스튜디오 컨설팅, AI 콘텐츠 제작 등 전문적인 이러닝 솔루션을 제공합니다.',
    address: {
      '@type': 'PostalAddress',
      addressCountry: 'KR',
      addressRegion: 'Seoul',
    },
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: '+82-2-6952-7400',
      contactType: 'customer service',
      availableLanguage: ['Korean'],
    },
    sameAs: [],
  };
};

export const createWebPageSchema = (name: string, description: string, url: string) => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  
  return {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name,
    description,
    url: `${siteUrl}${url}`,
    publisher: {
      '@type': 'Organization',
      name: 'KR STUDIO',
      url: siteUrl,
    },
  };
};

export const createArticleSchema = (title: string, description: string, datePublished: string, author?: string) => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  
  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: title,
    description,
    datePublished,
    author: {
      '@type': author ? 'Person' : 'Organization',
      name: author || 'KR STUDIO',
    },
    publisher: {
      '@type': 'Organization',
      name: 'KR STUDIO',
      url: siteUrl,
      logo: {
        '@type': 'ImageObject',
        url: `${siteUrl}/vite.svg`,
      },
    },
  };
};

export const createServiceSchema = (name: string, description: string) => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  
  return {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name,
    description,
    provider: {
      '@type': 'Organization',
      name: 'KR STUDIO',
      url: siteUrl,
    },
    areaServed: {
      '@type': 'Country',
      name: 'South Korea',
    },
  };
};
